import React, { useState } from 'react';
import Modal from '../shared/Modal';
import { TextField, SelectField, PasswordField } from '../shared/FormField';
import { buildUserPayload } from '../../../api/usersApi';
import { useLocationOptions } from '../shared/useLocationOptions';

const blank = {
  name: '',
  email: '',
  password: '',
  institution_uuid: '',
  access_level: 'national',
  state: '',
  lga: '',
};

// Must match the role assigned afterwards in Role Assignments: national roles
// → National; State Officer (R04) → one State; Field Officer (R05) → State + LGA.
const ACCESS_LEVEL_OPTIONS = [
  { value: 'national', label: 'National — all states (R01, R02, R03, R06)' },
  { value: 'state', label: 'State — one state (State Officer, R04)' },
  { value: 'lga', label: 'LGA — one LGA (Field Officer, R05)' },
];

const parseError = (error) =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.message ||
  'Unable to create user.';

export default function CreateUserModal({ isOpen, onClose, onCreate, institutions = [], existingRows = [] }) {
  const [form, setForm] = useState(blank);
  const [status, setStatus] = useState('idle');
  const [errMsg, setErrMsg] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { stateOptions, lgaOptions, loadingLgas } = useLocationOptions(form.state);

  const set = (k) => (v) => setForm((f) => {
    if (k === 'access_level') {
      return { ...f, access_level: v, state: v === 'national' ? '' : f.state, lga: v === 'lga' ? f.lga : '' };
    }
    if (k === 'state') {
      return { ...f, state: v, lga: '' };
    }
    return { ...f, [k]: v };
  });

  const institutionOptions = institutions.map((i) => ({ value: i.uuid ?? i.id, label: i.name }));

  const submit = async () => {
    if (submitting) return;
    if (!form.name.trim() || !form.email.trim() || !form.password || !form.institution_uuid) return;
    if (form.access_level !== 'national' && !form.state) {
      setErrMsg('Select the state for this user.');
      setStatus('error');
      return;
    }
    if (form.access_level === 'lga' && !form.lga) {
      setErrMsg('Select the LGA for this Field Officer.');
      setStatus('error');
      return;
    }
    const duplicate = existingRows.some(
      (r) => r.email?.toLowerCase() === form.email.trim().toLowerCase()
    );
    if (duplicate) {
      setErrMsg('A user with this email already exists in the system.');
      setStatus('error');
      return;
    }
    setSubmitting(true);
    setStatus('idle');
    setErrMsg('');
    try {
      const { access_level: accessLevel, ...fields } = form;
      await onCreate(buildUserPayload({
        ...fields,
        state: accessLevel === 'national' ? null : form.state,
        lga: accessLevel === 'lga' ? form.lga : null,
        name: form.name.trim(),
        email: form.email.trim(),
        institution_id: form.institution_uuid,
        is_active: true,
      }));
      setStatus('success');
    } catch (error) {
      setErrMsg(parseError(error));
      setStatus('error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setForm(blank);
    setStatus('idle');
    setErrMsg('');
    onClose();
  };

  return (
    <Modal
      title="Create User"
      isOpen={isOpen}
      onClose={handleClose}
      onSubmit={submit}
      status={status}
      errorMessage={errMsg}
      onContinue={handleClose}
      submitLabel="Create"
      successTitle="User Created"
      successMessage="The user was saved to the backend."
      isSubmitting={submitting}
    >
      <TextField
        label="Full Name"
        required
        value={form.name}
        onChange={set('name')}
        placeholder="Enter full name"
        autoComplete="off"
      />
      <TextField
        label="Email"
        required
        type="email"
        value={form.email}
        onChange={set('email')}
        placeholder="Enter email"
        autoComplete="off"
      />
      <PasswordField
        label="Password"
        required
        value={form.password}
        onChange={set('password')}
        placeholder="Enter password"
        autoComplete="new-password"
      />
      <SelectField
        label="Institution"
        required
        value={form.institution_uuid}
        onChange={set('institution_uuid')}
        options={institutionOptions}
      />
      <SelectField
        label="Access Level"
        required
        value={form.access_level}
        onChange={set('access_level')}
        options={ACCESS_LEVEL_OPTIONS}
        note="Assign the matching role in Role Assignments."
      />
      {form.access_level !== 'national' && (
        <SelectField
          label="State"
          required
          value={form.state}
          onChange={set('state')}
          options={stateOptions}
          placeholder="Select State"
          note={form.access_level === 'state' ? 'The one state this State Officer works in.' : undefined}
        />
      )}
      {form.access_level === 'lga' && (
        <SelectField
          label="LGA"
          required
          value={form.lga}
          onChange={set('lga')}
          options={lgaOptions}
          placeholder={form.state ? (loadingLgas ? 'Loading LGAs...' : 'Select LGA') : 'Select State first'}
          disabled={!form.state || loadingLgas}
          note="The Field Officer sees every ward in this LGA."
        />
      )}
    </Modal>
  );
}
